import cn from "classnames";
import type { MotionProps } from "framer-motion";
import { motion } from "framer-motion";
import Image from "next/image";
import type { FC, ButtonHTMLAttributes, ReactNode } from "react";

import { Spinner } from "./Spinner";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant: "primary" | "secondary";
  label?: string;
  icon?: ReactNode;
  size?: "large" | "medium" | "small";
  leftIcon?: string | ReactNode;
  leftIconSizePx?: number;
  rightIcon?: string | ReactNode;
  rightIconSizePx?: number;
  isLoading?: boolean;
  className?: string;
}

export const Button: FC<ButtonProps> = ({
  variant,
  className,
  label,
  icon,
  size = "large",
  leftIcon,
  leftIconSizePx = 24,
  rightIcon,
  rightIconSizePx = 24,
  isLoading = false,
  ...props
}) => {
  const baseStyle =
    "flex gap-2 items-center justify-center rounded-full px-28-px font-bold focus:outline-none";
  const smallStyle = "min-h-[32px] text-base";
  const mediumStyle = "min-h-[40px] text-base";
  const largeStyle = "min-h-[48px] text-lg";
  const primaryStyle =
    "bg-primary text-white active:bg-button-active hover:bg-button-hover disabled:bg-opacity-50 disabled:opacity-50";
  const secondaryStyle =
    "bg-transparent text-primary border border-primary active:border-button-active active:text-button-active hover:border-button-hover hover:text-button-hover disabled:text-opacity-50 disabled:opacity-50";

  const buttonStyle = cn(
    baseStyle,
    {
      [smallStyle]: size === "small",
      [mediumStyle]: size === "medium",
      [largeStyle]: size === "large",
      [primaryStyle]: variant === "primary",
      [secondaryStyle]: variant === "secondary",
    },
    className
  );

  return (
    <motion.button
      whileTap={{ scale: 0.95, animationDuration: "0.2s" }}
      className={buttonStyle}
      {...(props as MotionProps)}
    >
      {isLoading && <Spinner />}
      {typeof leftIcon === "string" ? (
        <Image
          src={leftIcon}
          className={`w-[${leftIconSizePx}] h-[${leftIconSizePx}]`}
          alt="button icon left"
          height={leftIconSizePx}
          width={leftIconSizePx}
        />
      ) : (
        leftIcon
      )}
      {label}
      {typeof rightIcon === "string" ? (
        <Image
          src={rightIcon}
          className={`w-[${rightIconSizePx}] h-[${rightIconSizePx}]`}
          alt="button icon right"
          height={rightIconSizePx}
          width={rightIconSizePx}
        />
      ) : (
        rightIcon
      )}
    </motion.button>
  );
};
